import { useState, type ReactNode } from 'react';
import type { Bulle } from '../partage/api/types';
import type { TempsAjoute } from './ChampsQuand';
import type { PieceJointe } from './ficheEnMots';
import { Plus } from './ReglagesDeLaFiche';
import { Ligne, Texte } from './Saisies';
import { entreeRange, quitteLaLigne } from './saisieTapee';
import {
  AUCUN_PLAN_CHOISI,
  CHOISIR_UN_PLAN,
  EN_DEVELOPPEMENT,
  PLANS_DE_VIE,
  SON_E_MAIL,
} from '../partage/vocabulaire';
import './PiecesDeLaFiche.css';

/**
 * Ce qui se joint et s'ajoute à une bulle, dans la fiche (chantier `revue-design`, ticket 02,
 * `design/fiche.html` et `design/fiche-vide.html`, décisions d'Igor du 2026-10-03, « La fiche »
 * 4 et 5) : le bloc « Plans de vie », sur la face ; les « + » du temps et les champs du lien, du
 * lieu et du contact, dans le panneau Réglages (chantier reglages-de-la-fiche, modifications 02 et
 * 03 : « Ajouter » et « Joint » ont quitté la face, chaque chose s'ajoute dans son intercalaire).
 *
 * Les plans de vie n'existent pas encore : ils s'affichent, et un clic dit « En cours de
 * développement, pas encore accessible ».
 */

/** Ce que la fiche ouvre : un temps à poser (Dates), une condition (la face), une pièce. */
export type Ajout = TempsAjoute | 'condition' | 'lien' | 'adresse' | 'contact';

/** Un mot qui dit qu'une chose montrée n'est pas encore faite, sous ce qu'on a cliqué. */
function EnDeveloppement() {
  return (
    <p className="fiche-en-dev" role="status">
      {EN_DEVELOPPEMENT}
    </p>
  );
}

/**
 * « Plans de vie » : aucun n'existe encore, la ligne dit « aucun plan choisi » et le bouton
 * répond qu'ils sont en cours de développement (décision 5). Aucun plan d'exemple : la fiche
 * n'affiche que ce qui est à toi.
 */
export function PlansDeVieDeLaFiche() {
  const [dit, setDit] = useState(false);
  return (
    <section className="plans-fiche">
      <div className="plans-fiche-tete">
        <span className="fiche-intertitre">{PLANS_DE_VIE}</span>
        <span className="plans-fiche-mot">{AUCUN_PLAN_CHOISI}</span>
      </div>
      <div className="plans-fiche-choix">
        <button type="button" className="piece-ajouter piece-ajouter--rond" aria-expanded={dit} onClick={() => setDit(!dit)}>
          <Plus />
          {CHOISIR_UN_PLAN}
        </button>
      </div>
      {dit && <EnDeveloppement />}
    </section>
  );
}

/**
 * Le lien, le lieu ou le contact qu'on pose : les champs des anciens paramètres, un à la fois,
 * dans le panneau du téléphone. Chacun envoie à la
 * sortie du champ ; Entrée, ou quitter l'encart, le range
 * (plus de « Fini », retours d'Igor du 2026-10-03). Passer de « Qui » à « Son numéro » ne le
 * range pas. Le tuto y
 * trouve ses lignes par leur nom (« Le lieu », « Le contact », « Le lien »).
 */
export function PieceAPoser({
  sorte,
  bulle,
  onChanger,
  onFini,
  avecEmail = false,
  children,
}: {
  sorte: 'lien' | 'adresse' | 'contact';
  bulle: Bulle;
  onChanger: (champs: Partial<Bulle>) => void;
  onFini: () => void;
  /** Le contact se pose avec son e-mail (la fiche Contact, qui le montre). */
  avecEmail?: boolean;
  /**
   * Ce qui se pose sous les champs, dans l'encart (au téléphone, « Ouvrir le lien », ticket 04 de
   * `telephone-projets-fiche`) : le toucher ne range pas l'encart avant d'avoir servi.
   */
  children?: ReactNode;
}) {
  return (
    <div
      className="piece-a-poser champs parametres"
      role="group"
      aria-label="pièce jointe"
      data-tuto="parametres"
      // Le champ s'enregistre en se quittant (`Texte`, Entrée le quitte aussi), puis l'encart se
      // range : sur Entrée, ou quand le focus part hors de l'encart.
      onKeyDown={(e) => {
        const cible = e.target as HTMLElement;
        if (entreeRange(e.key, cible.tagName, e.nativeEvent.isComposing)) onFini();
      }}
      onBlur={(e) => {
        if (quitteLaLigne(e.currentTarget, e.relatedTarget)) onFini();
      }}
    >
      {sorte === 'adresse' && (
        <Ligne nom="Le lieu">
          <Texte valeur={bulle.lieuTexte} place="Une adresse, un guichet…" auto onChanger={(lieuTexte) => onChanger({ lieuTexte })} />
        </Ligne>
      )}
      {/* Deux champs, comme en base : qui, et son numéro. */}
      {sorte === 'contact' && (
        <Ligne nom="Le contact">
          <div className="parametres-deux">
            <Texte valeur={bulle.contactNom} place="Qui" auto onChanger={(contactNom) => onChanger({ contactNom })} />
            <Texte valeur={bulle.contactTel} place="Son numéro" onChanger={(contactTel) => onChanger({ contactTel })} />
          </div>
        </Ligne>
      )}
      {/* L'e-mail, que la fiche Contact montre (types-de-fiche, modification 04). */}
      {sorte === 'contact' && avecEmail && (
        <Ligne nom={SON_E_MAIL}>
          <Texte valeur={bulle.contactEmail} place="nom@exemple.fr" onChanger={(contactEmail) => onChanger({ contactEmail })} />
        </Ligne>
      )}
      {sorte === 'lien' && (
        <Ligne nom="Le lien" aide="Une adresse qui commence par https://">
          <Texte valeur={bulle.lien} place="https://…" auto onChanger={(lien) => onChanger({ lien })} />
        </Ligne>
      )}
      {children}
    </div>
  );
}

/** Les icônes de la maquette : un trombone pour le lien, une épingle, une silhouette. */
export function IconeDePiece({ sorte }: { sorte: PieceJointe['sorte'] }) {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      {sorte === 'lien' && (
        <path d="M10 14a4 4 0 0 0 5.7 0l3-3a4 4 0 0 0-5.7-5.7l-1 1M14 10a4 4 0 0 0-5.7 0l-3 3a4 4 0 0 0 5.7 5.7l1-1" />
      )}
      {sorte === 'adresse' && (
        <>
          <path d="M12 21s-6-5.3-6-10a6 6 0 0 1 12 0c0 4.7-6 10-6 10z" />
          <circle cx="12" cy="11" r="2" />
        </>
      )}
      {sorte === 'contact' && (
        <>
          <circle cx="12" cy="8" r="3.5" />
          <path d="M5 20c0-3.5 3.1-6 7-6s7 2.5 7 6" />
        </>
      )}
    </svg>
  );
}
